import storeConstants from '@utils/constants/store';
import { dateConstants, orderConstants } from '@utils/constants';
import apiService from '@api/apiService';
import mutationTypes from '@helpers/storeFactories/helpers/mutationTypes';
import { storeUtils } from '@utils';
import LogService from '@services/LogService';
import { default as payment } from './ordersStatisticsPayment';
import { default as product } from './ordersStatisticsProduct';
import { default as loyaltyTransactions } from './ordersStatisticsLoyaltyTransactions';
import { default as salesTransactions } from './ordersStatisticsSalesTransactions';
import dateFilter from '@/utils/date';

const REFRESH_TIME = storeConstants.REFRESH_TIME;

const STORE_NAME = 'ordersStatistics';

const normalizeFilters = ({ data, partnerIds }) => {
    return {
        from: data.from
            ? dateFilter(new Date(data.from.date), dateConstants.FORMAT.API_DATE)
            : null,
        to: data.from ? dateFilter(new Date(data.to), dateConstants.FORMAT.API_DATE) : null,
        partnerIds: data.partnerIds === -1 ? partnerIds.join() : [data.partnerIds].join(),
    };
};

export default {
    name: STORE_NAME,
    namespaced: true,
    state: {
        filter: {},
        timestamp: 0,
        loading: 0,
        refresh: false,
        baseCurrency: null,
    },
    mutations: {
        [mutationTypes.SET_LOADING](state, loading) {
            state.loading = loading;
        },
        [mutationTypes.SET_DATA](state, data) {
            state.timestamp = new Date().getTime();
            state.baseCurrency = data.loyaltyTransactions.cashBackBaseCurrency || null;
        },
        [mutationTypes.SET_REFRESH](state, refresh) {
            state.refresh = refresh;
        },
        [mutationTypes.UPDATE_FILTERS](state, { filter }) {
            state.filter = filter;
        },
    },
    getters: {
        loading: (state) => state.loading,
        filter: (state) => state.filter,
        filterDateAggregateLevel: (state) => state.filter.from.aggregateLevel,
        isDataViewChart: (state) =>
            state.filter.switchDataView === orderConstants.STATISTICS.VIEW.CHART,
        isDataTypeRevenue: (state) =>
            state.filter.switchDataType === orderConstants.STATISTICS.TYPE.REVENUE,
    },
    actions: {
        get: async ({ commit, state, rootGetters }, { refresh = false, filter = {} } = {}) => {
            const needsRefresh =
                refresh ||
                state.refresh ||
                storeUtils.isStoreOutDated(state.timestamp, REFRESH_TIME);

            if (needsRefresh) {
                commit(`ordersStatistics/salesTransactions/${mutationTypes.CLEAR_DATA}`, null, {
                    root: true,
                });
                commit(`ordersStatistics/product/${mutationTypes.CLEAR_DATA}`, null, {
                    root: true,
                });
                commit(`ordersStatistics/payment/${mutationTypes.CLEAR_DATA}`, null, {
                    root: true,
                });
                commit(`ordersStatistics/loyaltyTransactions/${mutationTypes.CLEAR_DATA}`, null, {
                    root: true,
                });
                commit(mutationTypes.SET_LOADING, state.loading + 1);
                const response = await apiService.orders.statistics.get({
                    filter: normalizeFilters({
                        data: filter,
                        partnerIds: rootGetters['user/partners/getPartnersListIds'],
                    }),
                });
                commit(mutationTypes.SET_LOADING, state.loading - 1);

                if (response.success) {
                    commit(
                        `ordersStatistics/salesTransactions/${mutationTypes.SET_DATA}`,
                        response.data,
                        { root: true }
                    );
                    commit(`ordersStatistics/product/${mutationTypes.SET_DATA}`, response.data, {
                        root: true,
                    });
                    commit(`ordersStatistics/payment/${mutationTypes.SET_DATA}`, response.data, {
                        root: true,
                    });
                    commit(
                        `ordersStatistics/loyaltyTransactions/${mutationTypes.SET_DATA}`,
                        response.data,
                        { root: true }
                    );
                    commit(mutationTypes.SET_DATA, response.data);

                    return state;
                } else {
                    LogService.debug('problem loading ordersStatistics');
                    return false;
                }
            } else {
                return state;
            }
        },
        async updateFilters({ commit }, { filter }) {
            return commit(mutationTypes.UPDATE_FILTERS, { filter });
        },
        setLoading({ state, commit }, loading = true) {
            if (loading) {
                commit(mutationTypes.SET_LOADING, state.loading + 1);
            } else {
                commit(mutationTypes.SET_LOADING, state.loading - 1);
            }
        },
    },
    modules: {
        payment: payment,
        product: product,
        salesTransactions: salesTransactions,
        loyaltyTransactions: loyaltyTransactions,
    },
};
